import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Icon from "@mdi/react";
import { mdiMagnify, mdiClose } from "@mdi/js";
import { Button, CircularProgress, InputAdornment, TextField, Chip, Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import { clsx } from "@ismael1361/utils";
import { MemberLibraryServices, PublicMemberLibraryDTO, McpLibraryServices, PublicMcpDTO, NotistackServices } from "shared/services";
import { HandleError } from "shared/utils";
import styles from "./styles.module.scss";

/** Gera iniciais a partir do nome (até 2 letras). */
function getInitials(name: string): string {
	return name
		.split(/[\s-_]+/)
		.filter(Boolean)
		.map((w) => w[0].toUpperCase())
		.slice(0, 2)
		.join("");
}

const FILTERS = [
	{ key: "todos", label: "Todos" },
	{ key: "multi", label: "Profissionais (2+ MCPs)" },
	{ key: "uno", label: "Especialistas focados (1 MCP)" },
	{ key: "conversacional", label: "Conversacionais (0 MCP)" },
] as const;
type FilterKey = (typeof FILTERS)[number]["key"];

interface CatalogTabProps {
	workspaceId: string | null;
}

export const CatalogTab: React.FC<CatalogTabProps> = ({ workspaceId }) => {
	const [searchTerm, setSearchTerm] = useState("");
	const [filter, setFilter] = useState<FilterKey>("todos");
	const [specialists, setSpecialists] = useState<PublicMemberLibraryDTO[]>([]);
	const [mcps, setMcps] = useState<PublicMcpDTO[]>([]);
	const [loading, setLoading] = useState(true);
	const [selected, setSelected] = useState<PublicMemberLibraryDTO | null>(null);
	const timeRef = useRef<NodeJS.Timeout>(undefined);

	const loadData = useCallback(() => {
		if (!workspaceId) return;
		setLoading(true);
		clearTimeout(timeRef.current);
		timeRef.current = setTimeout(async () => {
			try {
				const [list, mcpList] = await Promise.all([MemberLibraryServices.listForWorkspace(workspaceId, true), McpLibraryServices.listMcps()]);
				setSpecialists(list);
				setMcps(mcpList);
			} catch (error) {
				NotistackServices.showErrorMessage(HandleError.isHandleError(error) ? error.message : "Erro ao carregar especialistas");
			} finally {
				setLoading(false);
			}
		}, 300);
	}, [workspaceId]);

	useEffect(() => {
		loadData();
		return () => clearTimeout(timeRef.current);
	}, [loadData]);

	const mcpById = useMemo(() => {
		const map = new Map<string, PublicMcpDTO>();
		for (const m of mcps) map.set(String(m.id), m);
		return map;
	}, [mcps]);

	const filtered = useMemo(() => {
		let result = specialists;
		if (filter === "multi") result = result.filter((s) => (s.mcpIds?.length ?? 0) >= 2);
		else if (filter === "uno") result = result.filter((s) => (s.mcpIds?.length ?? 0) === 1);
		else if (filter === "conversacional") result = result.filter((s) => (s.mcpIds?.length ?? 0) === 0);
		if (searchTerm.trim()) {
			const term = searchTerm.toLowerCase();
			result = result.filter((s) => s.name.toLowerCase().includes(term) || (s.description ?? "").toLowerCase().includes(term));
		}
		return result;
	}, [specialists, filter, searchTerm]);

	const renderMcpBadges = (mcpIds: string[]) => {
		if (!mcpIds?.length) return <span className={styles.noMcpBadge}>Sem ferramentas — apenas conversacional</span>;
		return (
			<div className={styles.mcpBadges}>
				{mcpIds.slice(0, 5).map((id) => {
					const mcp = mcpById.get(String(id));
					if (!mcp) return null;
					return (
						<Chip
							key={id}
							size="small"
							label={mcp.name}
							className={styles.mcpBadge}
							avatar={mcp.iconUrl ? <img src={mcp.iconUrl} alt={mcp.name} className={styles.mcpBadgeIcon} /> : undefined}
						/>
					);
				})}
				{mcpIds.length > 5 && <Chip size="small" label={`+${mcpIds.length - 5}`} className={styles.mcpBadgeMore} />}
			</div>
		);
	};

	return (
		<>
			<div className={styles.searchSection}>
				<TextField
					className={styles.searchInput}
					placeholder="Buscar por profissão, ferramenta, função..."
					variant="outlined"
					size="small"
					fullWidth
					value={searchTerm}
					onChange={(e) => setSearchTerm(e.target.value)}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<Icon path={mdiMagnify} size={0.95} className={styles.searchIcon} />
							</InputAdornment>
						),
					}}
				/>
				<div className={styles.filterRow}>
					{FILTERS.map((f) => (
						<Button
							key={f.key}
							className={clsx(styles.filterChip, filter === f.key ? styles.active : styles.inactive)}
							onClick={() => setFilter(f.key)}
							disableRipple
						>
							{f.label}
						</Button>
					))}
				</div>
			</div>

			{loading ? (
				<div className={styles.loadingContainer}>
					<CircularProgress size={32} />
				</div>
			) : filtered.length === 0 ? (
				<div className={styles.emptyContainer}>
					<p>Nenhum especialista encontrado</p>
				</div>
			) : (
				<div className={styles.grid}>
					{filtered.map((s) => {
						const nMcps = s.mcpIds?.length ?? 0;
						return (
							<div key={s.id} className={styles.card} onClick={() => setSelected(s)}>
								<div className={styles.cardHeader}>
									<div className={styles.cardAvatar}>
										<span>{getInitials(s.name)}</span>
									</div>
									<div className={styles.cardCount}>{nMcps === 0 ? "Conversacional" : nMcps === 1 ? "1 ferramenta" : `${nMcps} ferramentas`}</div>
								</div>
								<h3 className={styles.cardName}>{s.name}</h3>
								<p className={styles.cardDesc}>{s.description ?? "Sem descrição."}</p>
								{renderMcpBadges(s.mcpIds ?? [])}
							</div>
						);
					})}
				</div>
			)}

			<Dialog open={!!selected} onClose={() => setSelected(null)} maxWidth="sm" fullWidth PaperProps={{ className: styles.dialogPaper }}>
				{selected && (
					<>
						<DialogTitle className={styles.dialogTitle}>
							<div className={styles.dialogTitleRow}>
								<div className={styles.cardAvatar}>
									<span>{getInitials(selected.name)}</span>
								</div>
								<div className={styles.dialogTitleText}>
									<h3>{selected.name}</h3>
									<span className={styles.dialogTitleSub}>
										{(selected.mcpIds?.length ?? 0) === 0
											? "Especialista conversacional"
											: `${selected.mcpIds!.length} ferramenta(s) disponíveis`}
									</span>
								</div>
							</div>
							<IconButton onClick={() => setSelected(null)} size="small" className={styles.dialogClose}>
								<Icon path={mdiClose} size={0.9} />
							</IconButton>
						</DialogTitle>
						<DialogContent className={styles.dialogContent}>
							<section className={styles.dialogSection}>
								<h4>Sobre</h4>
								<p>{selected.description ?? "Sem descrição cadastrada."}</p>
							</section>
							<section className={styles.dialogSection}>
								<h4>Ferramentas (MCPs)</h4>
								{renderMcpBadges(selected.mcpIds ?? [])}
							</section>
							<section className={styles.dialogSection}>
								<h4>Comportamento (system prompt)</h4>
								<pre className={styles.systemPromptPre}>{selected.systemPrompt}</pre>
							</section>
							<section className={styles.dialogSection}>
								<p className={styles.dialogHint}>
									Para contratar este especialista para uma equipe, vá na aba <strong>Meus Contratados</strong> ou peça ao Assistente:{" "}
									<em>"Contratar o {selected.name} para a equipe X"</em>.
								</p>
							</section>
						</DialogContent>
					</>
				)}
			</Dialog>
		</>
	);
};
